import { gql, ApolloError } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import type { UsuarioConnection } from '../types/usuario';
import { ATLETAS_FILTRADOS } from './atletaService';

export interface Atleta {
  id: string;
  cpf?: string | null;
  telefone?: string | null;
  genero?: string | null;
  dataNascimento?: string | null;
}

export interface Organizador {
  id: string;
  nome: string;
  telefone?: string | null;
  nomeFuncao?: string | null;
  torneio: {
    id: string;
    nome: string;
  };
  liga?: {
    id: string;
    nome: string;
    username?: string | null;
    imagemUrl?: string | null;
    bannerUrl?: string | null;
  } | null;
}

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  username?: string | null;
  imagemUrl?: string | null;
  role: string;
  ativo: boolean;
  criadoEm?: string;
  atualizadoEm?: string;
  atleta?: Atleta | null;
  organizadores?: Organizador[];
}

export interface UsuarioInput {
  nome?: string;
  email?: string;
  username?: string;
  senha?: string;
  role?: string;
  ativo?: boolean;
}

export const ATUALIZAR_USUARIO = gql`
  mutation AtualizarUsuario($id: ID!, $input: UsuarioInput!) {
    atualizarUsuario(id: $id, input: $input) {
      id
      nome
      username
      email
      imagemUrl
      role
      ativo
    }
  }
`;

export const atualizarUsuario = async (
  id: string,
  input: UsuarioInput,
): Promise<Usuario> => {
  const { data } = await apolloClient.mutate<{ atualizarUsuario: Usuario }>({
    mutation: ATUALIZAR_USUARIO,
    variables: { id, input },
  });
  return data!.atualizarUsuario;
};

// ----------------------
// Buscar Usuário por ID
// ----------------------

export const USUARIO_POR_ID = gql`
  query UsuarioPorId($id: ID!) {
    usuarioPorId(id: $id) {
      id
      nome
      email
      username
      imagemUrl
      role
      ativo
      criadoEm
      atualizadoEm
      atleta {
        id
        cpf
        telefone
        genero
        dataNascimento
      }
      organizadores {
        id
        nome
        telefone
        nomeFuncao
        torneio {
          id
          nome
        }
        liga {
          id
          nome
          username
          imagemUrl
          bannerUrl
        }
      }
    }
  }
`;

export const usuarioPorId = async (id: string): Promise<Usuario> => {
  const { data } = await apolloClient.query<{ usuarioPorId: Usuario }>({
    query: USUARIO_POR_ID,
    variables: { id },
  });
  return data.usuarioPorId;
};

// ----------------------
// Buscar Usuários por Nome
// ----------------------

export const USUARIOS_POR_NOME = gql`
  query UsuariosPorNome($nome: String!) {
    usuariosPorNome(nome: $nome) {
      id
      nome
      email
      username
      imagemUrl
      role
      ativo
    }
  }
`;

export const buscarUsuarios = async (nome: string): Promise<Usuario[]> => {
  const { data } = await apolloClient.query<{ usuariosPorNome: Usuario[] }>({
    query: USUARIOS_POR_NOME,
    variables: { nome: nome?.trim() ?? '' },
    fetchPolicy: 'no-cache',
  });
  return data.usuariosPorNome;
};

// ----------------------
// Ativar/Desativar Usuário
// ----------------------

export interface UsuarioToggle {
  id: string;
  username?: string | null;
  ativo: boolean;
  __typename?: 'Usuario';
}

export const ATIVAR_USUARIO = gql`
  mutation AtivarUsuario($id: ID!) {
    ativarUsuario(id: $id) {
      id
      username
      ativo
    }
  }
`;

export const DESATIVAR_USUARIO = gql`
  mutation DesativarUsuario($id: ID!) {
    desativarUsuario(id: $id) {
      id
      username
      ativo
    }
  }
`;

function updateUsuarioCache(id: string, ativo: boolean, username?: string | null) {
  try {
    const cacheId = apolloClient.cache.identify({ __typename: 'Usuario', id });
    if (cacheId) {
      apolloClient.cache.modify({
        id: cacheId,
        fields: {
          ativo: () => ativo,
          username: () => username,
        },
      });
    }

    // Best-effort: atualiza listas em cache se existirem (atletasFiltrados)
    // Em muitas telas usamos fetchPolicy 'no-cache', então isso pode não existir.
    apolloClient.cache.updateQuery<any>({ query: ATLETAS_FILTRADOS }, (data) => {
      if (!data?.atletasFiltrados?.items) return data;
      const items = data.atletasFiltrados.items.map((it: any) => {
        if (it?.usuario?.id === id) {
          return {
            ...it,
            usuario: {
              ...it.usuario,
              ativo,
              username: typeof username !== 'undefined' ? username : it.usuario?.username,
              __typename: 'Usuario',
            },
          };
        }
        return it;
      });
      return { ...data, atletasFiltrados: { ...data.atletasFiltrados, items } };
    });
  } catch {
    // Silencia erros de cache; o fallback é refetch na camada superior
  }
}

export const activateUsuario = async (id: string): Promise<UsuarioToggle> => {
  const { data } = await apolloClient.mutate<{ ativarUsuario: UsuarioToggle }, { id: string}>({
    mutation: ATIVAR_USUARIO,
    variables: { id },
    optimisticResponse: {
      ativarUsuario: { id, username: null, ativo: true, __typename: 'Usuario' },
    },
    update: (_cache, result) => {
      const ativo = result.data?.ativarUsuario?.ativo ?? true;
      const username = result.data?.ativarUsuario?.username;
      updateUsuarioCache(id, ativo, username);
    },
  });
  const user = data!.ativarUsuario;
  updateUsuarioCache(id, user.ativo, user.username);
  return user;
};

export const deactivateUsuario = async (id: string): Promise<UsuarioToggle> => {
  const { data } = await apolloClient.mutate<{ desativarUsuario: UsuarioToggle }, { id: string}>({
    mutation: DESATIVAR_USUARIO,
    variables: { id },
    optimisticResponse: {
      desativarUsuario: { id, username: null, ativo: false, __typename: 'Usuario' },
    },
    update: (_cache, result) => {
      const ativo = result.data?.desativarUsuario?.ativo ?? false;
      const username = result.data?.desativarUsuario?.username;
      updateUsuarioCache(id, ativo, username);
    },
  });
  const user = data!.desativarUsuario;
  updateUsuarioCache(id, user.ativo, user.username);
  return user;
};

// ----------------------
// Alterar Senha Admin
// ----------------------

export const ALTERAR_SENHA_ADMIN = gql`
  mutation AlterarSenhaAdmin($id: ID!, $novaSenha: String!) {
    alterarSenhaAdmin(id: $id, novaSenha: $novaSenha)
  }
`;

export const alterarSenhaAdmin = async (id: string, novaSenha: string): Promise<boolean> => {
  const { data } = await apolloClient.mutate<{ alterarSenhaAdmin: boolean }>({
    mutation: ALTERAR_SENHA_ADMIN,
    variables: { id, novaSenha },
  });
  return data!.alterarSenhaAdmin;
};

// ----------------------
// Listar Usuários Admin (filtrados)
// ----------------------

function getErrorMessage(error: unknown): string {
  if (error instanceof ApolloError) {
    return error.graphQLErrors[0]?.message || error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Erro inesperado.';
}

export const USUARIOS_FILTRADOS = gql`
  query UsuariosFiltrados(
    $nome: String
    $email: String
    $username: String
    $role: String
    $ativo: Boolean
    $page: Int
    $size: Int
    $sortBy: String
    $direction: String
  ) {
    usuariosFiltrados(
      nome: $nome
      email: $email
      username: $username
      role: $role
      ativo: $ativo
      page: $page
      size: $size
      sortBy: $sortBy
      direction: $direction
    ) {
      items {
        id
        nome
        username
        email
        imagemUrl
        role
        ativo
        criadoEm
        atualizadoEm
      }
      page
      size
      totalItems
      totalPages
    }
  }
`;

export async function listarUsuariosAdmin(
  params?: {
    page?: number;
    size?: number;
    nome?: string;
    email?: string;
    ativo?: boolean;
  },
): Promise<UsuarioConnection> {
  try {
    const { data } = await apolloClient.query<
      { usuariosFiltrados: UsuarioConnection },
      {
        page?: number;
        size?: number;
        nome?: string;
        email?: string;
        role: string;
        ativo?: boolean;
      }
    >({
      query: USUARIOS_FILTRADOS,
      variables: {
        page: params?.page,
        size: params?.size,
        nome: params?.nome,
        email: params?.email,
        role: 'ADMIN',
        ativo: params?.ativo,
      },
      fetchPolicy: 'no-cache',
    });
    return data.usuariosFiltrados;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

// ----------------------
// Criar Usuário
// ----------------------

export const CRIAR_USUARIO = gql`
  mutation CriarUsuario($input: UsuarioInput!) {
    criarUsuario(input: $input) {
      id
      nome
      username
      email
      imagemUrl
      role
      ativo
    }
  }
`;

export async function criarUsuario(input: UsuarioInput): Promise<Usuario> {
  try {
    const { data } = await apolloClient.mutate<
      { criarUsuario: Usuario },
      { input: UsuarioInput }
    >({
      mutation: CRIAR_USUARIO,
      variables: { input },
    });
    if (!data?.criarUsuario) {
      throw new Error('Erro ao criar usuário.');
    }
    return data.criarUsuario;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

